import type { Observable } from 'rxjs';
import type { CustomFieldCatalog } from './lib/custom-field-catalog.types';

/**
 * Port: resolves the custom-field catalog (groups → definitions → options) for one consuming
 * entity type. `@aurora` owns only this abstract contract (HR-PORTS-001) — the concrete adapter
 * queries the `tools` bounded context's admin GraphQL API and therefore lives in APP code
 * (`app/domains/admin/apps/tools/custom-field-definition/data-access/`), wired via
 * `{ provide: CustomFieldCatalogService, useClass: <adapter> }` in `app/aurora.provider.ts`.
 * Mirrors the `CurrentAccountService` port/adapter pattern (`@aurora/modules/authentication`).
 *
 * The generated form, form-embed and list of a `hasCustomFields` module inject this port
 * directly — never the `tools` domain — so they compile and run unmodified in an application
 * scaffold that has not installed `tools` (in which case NO binding exists in
 * `aurora.provider.ts`, and injecting the port without one is a DI configuration error the
 * consuming app must fix by adding the binding, per `complete-custom-fields-capability`'s D7).
 *
 * @example
 * const catalog = toSignal(inject(CustomFieldCatalogService).fetch('IamTag'), { initialValue: { groups: [] } });
 */
export abstract class CustomFieldCatalogService {
  /**
   * Resolves every group associated with `entityType` (a group's `entityTypes` array-contains
   * it), each with its definitions and, for SELECT/MULTISELECT definitions, their full option
   * catalog. An entity type with no associated groups resolves to `{ groups: [] }`.
   *
   * @param entityType - The consuming aggregate's registered entity type (its `aggregateName`).
   */
  abstract fetch(entityType: string): Observable<CustomFieldCatalog>;
}
